import { useCallback } from 'react';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useDecisionMakerRecommender } from 'src/app/shared/hooks/decisionMakers';

import { NOTIFICATION_TYPES } from '../../../../constants';

type Props = {
	onDismiss?: () => void;
};

type UseDecisionMakerNotificationReturn = ModelHookResult<
	{ numDecisionMakers: number },
	{ handleOpenModal: () => Promise<void>; handleDismiss: () => void }
>;

type RecommendedDecisionMakerListResponse = StarterDashboardV2PageData['petition']['recommendedDecisionMakers'];
type RecommendedDecisionMakerResponse = RecommendedDecisionMakerListResponse[0];

type ModalOptions = Parameters<ReturnType<typeof useDecisionMakerRecommender>>[0];
type RecommendedDecisionMakerList = ModalOptions['recommendedDecisionMakers'];

const MAX_RECOMMENDATIONS = 3;

export function useDecisionMakerNotification({ onDismiss }: Props = {}): UseDecisionMakerNotificationReturn {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const {
		data: { petition },
		actions: { refreshData },
	} = useStarterDashboardV2PageData();

	const track = useTracking();

	const openDecisionMakerRecommenderModal = useDecisionMakerRecommender();

	const transformedDecisionMakers: RecommendedDecisionMakerList = petition.recommendedDecisionMakers
		.map((dm: RecommendedDecisionMakerResponse) => ({
			normalizedId: dm.normalizedDmId,
			canonicalPetitionTargetId: dm.canonicalPetitionTargetId,
			name: dm.name,
			title: dm.title,
			type: dm.type,
			photoUrl: dm.photo?.petitionMedium.url || null,
		}))
		.slice(0, MAX_RECOMMENDATIONS);

	const numDecisionMakers = Math.min(MAX_RECOMMENDATIONS, petition.recommendedDecisionMakers.length);

	const handleOpenModal = useCallback(async () => {
		void track('petition_manage_notification_cta_click', {
			notification: NOTIFICATION_TYPES.decisionMakerRecommendation,
		});

		const modalOptions: ModalOptions = {
			petitionSlug: slug,
			petitionId: petition.id,
			recommendedDecisionMakers: transformedDecisionMakers,
			trackingContext: 'dashboard',

			onClose: refreshData,
		};
		await openDecisionMakerRecommenderModal(modalOptions);
	}, [track, slug, petition.id, transformedDecisionMakers, refreshData, openDecisionMakerRecommenderModal]);

	const handleDismiss = useCallback(() => {
		onDismiss?.();
	}, [onDismiss]);

	return {
		data: { numDecisionMakers },
		actions: { handleOpenModal, handleDismiss },
	};
}
